import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { ArrowLeft, CheckCircle, Sparkles, CreditCard, Shield, Clock, Zap, BarChart3, Cpu, HeadphonesIcon } from 'lucide-react';
import API_BASE_URL from '../config/api';

// Use specific payment API URL if available, otherwise fallback to central config
const API_URL = import.meta.env.VITE_PAYMENT_API_URL || API_BASE_URL;

// Initialize Stripe - Only if key is provided (prevents errors)
// Memoize the promise to avoid re-initialization
let stripePromiseInstance = null;

const getStripePromise = () => {
  if (stripePromiseInstance !== null) {
    return stripePromiseInstance;
  }

  try {
    const key = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY;
    if (key && typeof key === 'string' && key.startsWith('pk_') && key.length > 10) {
      stripePromiseInstance = loadStripe(key);
      return stripePromiseInstance;
    }
  } catch (error) {
    console.warn('Stripe not configured:', error);
  }

  stripePromiseInstance = null;
  return null;
};

function PaymentForm({ selectedPlan, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    // Confirm payment
    const { error: confirmError } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/payment-success`,
      },
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="border border-slate-200 rounded-xl p-6 bg-white shadow-sm min-h-[300px]">
        <PaymentElement
          options={{
            layout: 'tabs',
          }}
        />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-5 py-4 rounded-xl text-sm">
          {error}
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || processing}
        className="w-full bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-[1.01] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3 text-lg"
      >
        {processing ? (
          <>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            Processing Payment...
          </>
        ) : (
          <>
            <Sparkles className="w-6 h-6" />
            Complete Payment
          </>
        )}
      </button>
    </form>
  );
}

function PaymentFormWrapper({ selectedPlan, onBack }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  // Check Stripe configuration first - before attempting to create payment intent
  const stripePromise = getStripePromise();

  useEffect(() => {
    // Don't attempt to create payment intent if Stripe is not configured
    if (!stripePromise) {
      setLoading(false);
      setError('Stripe publishable key is not configured');
      return;
    }

    let isMounted = true;
    let timeoutId;
    let abortController;

    const createPaymentIntent = async () => {
      try {
        setLoading(true);
        setError(null);

        // Create abort controller for timeout
        abortController = new AbortController();
        timeoutId = setTimeout(() => abortController.abort(), 25000); // 25 second timeout

        const response = await fetch(`${API_URL}/api/payments/create-intent`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            plan: selectedPlan, // 'annual' or 'monthly'
          }),
          signal: abortController.signal,
        });

        clearTimeout(timeoutId);

        if (!isMounted) return;

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({ error: 'Server error' }));
          setError(errorData.error || errorData.message || `Server error (${response.status})`);
          setLoading(false);
          return;
        }

        const data = await response.json();
        if (data.success && data.clientSecret) {
          setClientSecret(data.clientSecret);
        } else {
          setError(data.error || data.message || 'Failed to initialize payment');
        }
      } catch (err) {
        if (!isMounted) return;

        if (err.name === 'AbortError') {
          setError('Request timed out. Please try again.');
        } else if (err.message) {
          setError(err.message);
        } else {
          setError('Failed to connect to payment server. Please check your connection and ensure the backend is running.');
        }
        console.error('Payment intent creation error:', err);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    createPaymentIntent();

    return () => {
      isMounted = false;
      if (timeoutId) clearTimeout(timeoutId);
      if (abortController) abortController.abort();
    };
  }, [selectedPlan, stripePromise, retryCount]);

  // Check Stripe configuration first
  if (!stripePromise) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 px-6 py-5 rounded-xl flex-1 flex flex-col justify-center">
        <p className="font-semibold mb-2 text-lg">Stripe Configuration Required</p>
        <p className="text-sm mb-3">Please set VITE_STRIPE_PUBLISHABLE_KEY in your .env.local file</p>
        <p className="text-xs">See STRIPE_SETUP.md for instructions</p>
        <p className="text-xs mt-2 text-slate-600">Note: The payment form requires a valid Stripe publishable key to load.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-14 w-14 border-b-3 border-emerald-600 mx-auto mb-5"></div>
          <p className="text-slate-600 text-lg">Loading secure payment form...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-5 rounded-xl mb-6">
        <p className="font-semibold mb-3 text-lg">Payment Setup Error</p>
        <p className="text-sm mb-4">{error}</p>
        <button
          onClick={() => {
            setError(null);
            setLoading(true);
            setClientSecret(null);
            // Increment retryCount to trigger useEffect
            setRetryCount(prev => prev + 1);
          }}
          className="text-sm underline hover:no-underline text-red-800 font-semibold hover:text-red-900 transition-colors"
        >
          Retry Connection
        </button>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <p className="text-slate-600">Initializing secure payment form...</p>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'stripe',
          variables: {
            colorPrimary: '#10b981', // Emerald green
            colorBackground: '#ffffff',
            colorText: '#1e293b',
            colorDanger: '#ef4444',
            fontFamily: 'system-ui, sans-serif',
            spacingUnit: '5px',
            borderRadius: '12px',
          },
        },
      }}
    >
      <PaymentForm selectedPlan={selectedPlan} onBack={onBack} />
    </Elements>
  );
}

export default function PaymentPage({ onBack }) {
  const [selectedPlan, setSelectedPlan] = useState('annual'); // Default to annual

  const plans = {
    annual: {
      name: 'Annual Plan',
      price: 290,
      pricePerMonth: 24,
      interval: 'year',
      savings: '17%',
      description: '€290 payable today',
      popular: true,
    },
    monthly: {
      name: 'Monthly Plan',
      price: 29,
      pricePerMonth: 29,
      interval: 'month',
      savings: null,
      description: '€29 payable today',
      popular: false,
    },
  };

  const currentPlan = plans[selectedPlan];

  const features = [
    { icon: Zap, title: 'Unlimited Analyses', description: 'Analyze as many properties as you need' },
    { icon: BarChart3, title: 'Advanced Analytics', description: 'Deep insights and market trends' },
    { icon: Cpu, title: 'API Access', description: 'Integrate with your own tools' },
    { icon: HeadphonesIcon, title: 'Priority Support', description: '24/7 dedicated assistance' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-emerald-50/30 to-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors mb-4 group"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            <span className="text-sm font-medium">Back to Dashboard</span>
          </button>

          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 bg-emerald-100 text-emerald-800 px-3 py-1.5 rounded-full text-xs font-semibold mb-3">
              <Sparkles className="w-3.5 h-3.5" />
              Upgrade to Premium
            </div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2 bg-gradient-to-r from-slate-900 to-emerald-900 bg-clip-text text-transparent">
              Unlock Your Full Potential
            </h1>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Get unlimited access to all premium features and advanced analytics
            </p>
          </div>
        </div>

        {/* Main Content - Equal Height Cards */}
        <div className="grid lg:grid-cols-5 gap-6 items-stretch">
          {/* Plan Selection - 2 columns */}
          <div className="lg:col-span-2 flex">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full flex flex-col">
              <h2 className="text-xl font-bold text-slate-900 font-heading mb-4">
                Choose Your Plan
              </h2>

              <div className="space-y-3 mb-6">
                {/* Annual Plan */}
                <button
                  onClick={() => setSelectedPlan('annual')}
                  className={`relative w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPlan === 'annual'
                    ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-white shadow-lg scale-[1.01]'
                    : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                    }`}
                >
                  {plans.annual.popular && (
                    <div className="absolute -top-2.5 left-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white px-2.5 py-0.5 rounded-full text-xs font-bold shadow-md">
                      MOST POPULAR
                    </div>
                  )}
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-base text-slate-900">Annual Billing</span>
                    {selectedPlan === 'annual' && (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-1">
                    €{plans.annual.pricePerMonth}
                    <span className="text-sm font-normal text-slate-600"> / month</span>
                  </div>
                  <div className="text-xs text-slate-600 mb-1.5">€{plans.annual.price} billed annually</div>
                  <div className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-700 px-2.5 py-0.5 rounded-full text-xs font-bold">
                    <Sparkles className="w-3 h-3" />
                    Save {plans.annual.savings}
                  </div>
                </button>

                {/* Monthly Plan */}
                <button
                  onClick={() => setSelectedPlan('monthly')}
                  className={`w-full text-left p-4 rounded-xl border-2 transition-all ${selectedPlan === 'monthly'
                    ? 'border-emerald-500 bg-gradient-to-br from-emerald-50 to-white shadow-lg scale-[1.01]'
                    : 'border-slate-200 hover:border-emerald-300 hover:bg-slate-50'
                    }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-bold text-base text-slate-900">Monthly Billing</span>
                    {selectedPlan === 'monthly' && (
                      <CheckCircle className="w-5 h-5 text-emerald-600" />
                    )}
                  </div>
                  <div className="text-2xl font-bold text-slate-900 mb-1">
                    €{plans.monthly.pricePerMonth}
                    <span className="text-sm font-normal text-slate-600"> / month</span>
                  </div>
                  <div className="text-xs text-slate-600">Billed monthly</div>
                </button>
              </div>

              {/* Features Grid */}
              <div className="border-t border-slate-200 pt-4 flex-1">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-wide mb-3">
                  What's Included
                </h3>
                <div className="space-y-3">
                  {features.map((feature, index) => {
                    const Icon = feature.icon;
                    return (
                      <div key={index} className="flex items-start gap-2.5">
                        <div className="flex-shrink-0 w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center">
                          <Icon className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-slate-900">{feature.title}</div>
                          <div className="text-xs text-slate-600">{feature.description}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Payment Form - 3 columns */}
          <div className="lg:col-span-3 flex">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-200 p-6 w-full flex flex-col">
              <div className="mb-5">
                <h2 className="text-2xl font-bold text-slate-900 font-heading mb-2">
                  Payment Details
                </h2>
                <p className="text-sm text-slate-600 flex items-center gap-2">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  Secured and encrypted by Stripe
                </p>
              </div>

              {/* Order Summary */}
              <div className="bg-gradient-to-br from-slate-50 to-emerald-50/30 rounded-xl p-4 mb-5 border border-slate-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm text-slate-700 font-medium">{currentPlan.name}</span>
                  <span className="text-base font-bold text-slate-900">
                    €{currentPlan.pricePerMonth} / month
                  </span>
                </div>
                <div className="text-xs text-slate-600 mb-3">{currentPlan.description}</div>
                {currentPlan.savings && (
                  <div className="bg-emerald-100 border border-emerald-300 text-emerald-800 px-3 py-1.5 rounded-lg text-xs font-semibold mb-3 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    Save {currentPlan.savings} with annual billing
                  </div>
                )}
                <div className="border-t border-slate-300 pt-3 flex justify-between items-center">
                  <span className="text-base font-bold text-slate-900">Total Due Today</span>
                  <span className="text-2xl font-bold text-emerald-600">€{currentPlan.price}</span>
                </div>
              </div>

              {/* Stripe Payment Element */}
              <div className="flex-1 flex flex-col mb-5">
                <PaymentFormWrapper selectedPlan={selectedPlan} onBack={onBack} />
              </div>

              {/* Trust Indicators */}
              <div className="pt-5 border-t border-slate-200">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Shield className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-slate-900">Secure Payment</span>
                    <span className="text-xs text-slate-600">256-bit SSL</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <Clock className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-slate-900">Money-Back</span>
                    <span className="text-xs text-slate-600">14-day guarantee</span>
                  </div>
                  <div className="flex flex-col items-center gap-1.5">
                    <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                      <CreditCard className="w-5 h-5 text-emerald-600" />
                    </div>
                    <span className="text-xs font-semibold text-slate-900">Flexible</span>
                    <span className="text-xs text-slate-600">Cancel anytime</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
